'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Select } from 'foundations-components/transitional/components/Select';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { Button } from 'foundations-components/transitional/components/Button';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Slider } from 'foundations-components/transitional/components/Slider';
import { View } from 'foundations-components/transitional/components/View';
import { useCallback, useEffect, useState } from 'react';
import { ConversionAssetPosition, ConversionAssetTypes } from '../../constants/video';
import CustomTimeInput from './CustomTimeInput';
import VideoPlayer from './VideoPlayer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CONVERSION_ASSET_DEFAULT_OPTIONS = {
  position: ConversionAssetPosition.PRE,
  overlay_delay_seconds: 0
};
const RECOMMENDED_POSITION_VALUE = 'RECOMMENDED';
const RECOMMENDED_OVERLAY_DELAY_PERCENTAGE = 0.15;
const getPositionOptions = () => {
  const PRE_POSITION_OPTION = {
    value: ConversionAssetPosition.PRE,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.pre')
  };
  const POST_POSITION_OPTION = {
    value: ConversionAssetPosition.POST,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.post')
  };
  const CUSTOM_POSITION_OPTION = {
    value: ConversionAssetPosition.CUSTOM,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.custom')
  };
  const RECOMMENDED_POSITION_OPTION = {
    value: RECOMMENDED_POSITION_VALUE,
    text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.position.options.recommended')
  };
  return [RECOMMENDED_POSITION_OPTION, PRE_POSITION_OPTION, POST_POSITION_OPTION, CUSTOM_POSITION_OPTION];
};
const getPreventSkipOptions = type => {
  if (type === ConversionAssetTypes.FORM) {
    return [{
      value: false,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.canSkipForm')
    }, {
      value: true,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.preventSkipForm')
    }];
  }
  if (type === ConversionAssetTypes.CTA) {
    return [{
      value: false,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.canSkipCta')
    }, {
      value: true,
      text: I18n.text('RichTextLib.video.conversionAssetPanel.fields.preventSkip.options.preventSkipCta')
    }];
  }
  return [];
};
const ConversionAssetOptionsPanel = ({
  conversionAsset,
  video,
  onSubmit,
  onClose,
  trackVideoInteraction = () => {}
}) => {
  const [conversionAssetState, setConversionAsset] = useState(Object.assign({}, CONVERSION_ASSET_DEFAULT_OPTIONS, conversionAsset));
  const [currentTime, setCurrentTime] = useState(conversionAsset.overlay_delay_seconds || 0);
  useEffect(() => {
    trackVideoInteraction('open-conversion-options');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    id,
    type
  } = conversionAssetState;
  const recommendedPosition = Math.floor(RECOMMENDED_OVERLAY_DELAY_PERCENTAGE * (video.duration / 1000));
  const selectedPositionOption = conversionAssetState.position === ConversionAssetPosition.CUSTOM && recommendedPosition === currentTime ? RECOMMENDED_POSITION_VALUE : conversionAssetState.position;
  const handlePositionChange = useCallback(({
    target: {
      value
    }
  }) => {
    trackVideoInteraction(`select-position-${value.toLowerCase()}`);
    if (value === RECOMMENDED_POSITION_VALUE) {
      value = ConversionAssetPosition.CUSTOM;
      setCurrentTime(recommendedPosition);
    }
    const updatedAsset = Object.assign({}, conversionAssetState, {
      position: value
    });
    setConversionAsset(updatedAsset);
  }, [conversionAssetState, recommendedPosition, trackVideoInteraction]);
  const handlePreventSkipChange = useCallback(({
    target: {
      value
    }
  }) => {
    trackVideoInteraction(`select-overlay-${value ? 'prevent' : 'allow'}-skip`);
    setConversionAsset(Object.assign({}, conversionAssetState, {
      prevent_skip: value
    }));
  }, [conversionAssetState, trackVideoInteraction]);
  const handleSubmit = () => {
    if (conversionAssetState.position === ConversionAssetPosition.CUSTOM) {
      if (currentTime) {
        conversionAssetState.overlay_delay_seconds = currentTime;
      } else {
        conversionAssetState.position = ConversionAssetPosition.PRE;
        delete conversionAssetState.overlay_delay_seconds;
      }
    }
    if (conversionAssetState.overlay_delay_seconds === 0) {
      delete conversionAssetState.overlay_delay_seconds;
    }
    trackVideoInteraction('submit-conversion-options');
    onSubmit(conversionAssetState);
  };
  const handleClose = () => {
    trackVideoInteraction('close-conversion-options');
    onClose();
  };
  return /*#__PURE__*/_jsxs(Panel, {
    width: 600,
    "data-test-id": "conversion-asset-options-panel",
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: handleClose
    }), /*#__PURE__*/_jsx(PanelHeader, {
      children: /*#__PURE__*/_jsx("h2", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.header'
        })
      })
    }), /*#__PURE__*/_jsxs(PanelSection, {
      blockSize: "100%",
      children: [id && video && /*#__PURE__*/_jsxs(View, {
        marginBlock: "400",
        children: [/*#__PURE__*/_jsx(VideoPlayer, {
          video: video,
          portalId: video.portalId,
          conversionAsset: conversionAssetState,
          maxHeight: 300,
          currentTime: currentTime,
          hiddenControls: conversionAssetState.position === ConversionAssetPosition.CUSTOM
        }), conversionAssetState.position === ConversionAssetPosition.CUSTOM && video.duration && /*#__PURE__*/_jsx(View, {
          paddingInline: "400",
          paddingBlock: "300",
          backgroundColor: "fill-accent-neutral-hover",
          children: /*#__PURE__*/_jsx(Slider, {
            min: 0,
            max: Math.floor(video.duration / 1000),
            value: currentTime,
            onChange: e => {
              trackVideoInteraction('change-overlay-delay-via-slider');
              setCurrentTime(e.target.value);
            }
          })
        })]
      }), conversionAssetState.id && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.position.label'
        }),
        children: /*#__PURE__*/_jsx(Select, {
          options: getPositionOptions(),
          value: selectedPositionOption,
          onChange: handlePositionChange,
          "data-test-id": "conv-asset-panel-position-select"
        })
      }), conversionAssetState.position === ConversionAssetPosition.CUSTOM && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.delaySeconds.label'
        }),
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.delaySeconds.tooltip'
        }),
        children: /*#__PURE__*/_jsx(CustomTimeInput, {
          value: currentTime,
          video: video,
          onChange: setCurrentTime,
          trackVideoInteraction: trackVideoInteraction
        })
      }), id && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.fields.preventSkip.label'
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `RichTextLib.video.conversionAssetPanel.fields.preventSkip.help.${type}`
        }),
        children: /*#__PURE__*/_jsx(Select, {
          options: getPreventSkipOptions(type),
          value: conversionAssetState.prevent_skip || false,
          onChange: handlePreventSkipChange,
          "data-test-id": "conv-asset-panel-prevent-skip-select"
        })
      })]
    }), /*#__PURE__*/_jsxs(PanelFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: handleSubmit,
        "data-test-id": "submit-conversion-asset-options",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.footer.done'
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.conversionAssetPanel.footer.cancel'
        })
      })]
    })]
  });
};
export default ConversionAssetOptionsPanel;